// Reflect's reflections as synced notes (tag "reflection"), so they follow the reader to every
// device and are encrypted at rest on the server. The note's title is what was reflected on, its
// body is Markdown (sync.md: Markdown is the sync format for notes): the quote, if any, then each
// answered prompt under its own heading. The anchor says where it sits: a verse, or a book section.
import type { Reflection, ReflectionSource } from "../data/study";
import { newNote, REFLECTION_TAG, type Note } from "./domain";
import type { Change } from "./ops";
import type { Anchor } from "./types";

const SOURCE_TAG = { scripture: "on:scripture", quote: "on:quote", section: "on:section" } as const;

function anchorFor(r: Pick<Reflection, "refKey" | "source">): { libraryId: string | null; anchor: Anchor | null } {
  const src = r.source ?? { kind: "scripture" as const };
  if (src.kind === "scripture") {
    const [from, to] = r.refKey.split("-");
    return { libraryId: null, anchor: from ? { verseKey: from.slice(0, 64), ...(to ? { endVerseKey: to.slice(0, 64) } : {}) } : null };
  }
  if (src.kind === "section") return { libraryId: src.libraryId, anchor: { sectionUid: src.sectionUid.slice(0, 200) } };
  return { libraryId: src.libraryId, anchor: src.sectionUid ? { sectionUid: src.sectionUid.slice(0, 200) } : null };
}

export function reflectionBody(r: Pick<Reflection, "answers" | "source">): string {
  const parts: string[] = [];
  if (r.source?.kind === "quote") parts.push(`> ${r.source.text.replace(/\n/g, "\n> ")}\n>\n> ${r.source.author}, *${r.source.work}*`);
  for (const a of r.answers) parts.push(`## ${a.prompt}\n\n${a.text}`);
  return parts.join("\n\n");
}

/** A reflection as a note change: a new note, or (with `id`) the same note kept under its id. */
export function reflectionToNote(r: Omit<Reflection, "id" | "date"> & { date?: string }, now = Date.now()): Change {
  const { libraryId, anchor } = anchorFor(r);
  const kind = r.source?.kind ?? "scripture";
  const created = r.date ? Date.parse(`${r.date}T12:00:00Z`) : now;
  const change = newNote({ libraryId, anchor, title: r.refLabel.slice(0, 500), body: reflectionBody(r), tags: [REFLECTION_TAG, SOURCE_TAG[kind]] }, now);
  if (Number.isFinite(created)) change.fields = { ...change.fields, createdAt: created };
  return change;
}

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** A note tagged "reflection" read back as a reflection. */
export function noteToReflection(n: Note): Reflection {
  const kind: ReflectionSource["kind"] = n.tags.includes(SOURCE_TAG.section) ? "section" : n.tags.includes(SOURCE_TAG.quote) ? "quote" : "scripture";
  const chunks = n.body.split(/^## /m);
  const head = chunks.shift() ?? "";
  const answers = chunks.map((c) => {
    const nl = c.indexOf("\n");
    return { prompt: (nl < 0 ? c : c.slice(0, nl)).trim(), text: (nl < 0 ? "" : c.slice(nl)).trim() };
  });
  const a = n.anchor;
  let source: ReflectionSource = { kind: "scripture" };
  let refKey = a?.verseKey ? `${a.verseKey}${a.endVerseKey ? `-${a.endVerseKey}` : ""}` : (n.title ?? "");
  if (kind === "section" && n.libraryId && a?.sectionUid) {
    source = { kind: "section", libraryId: n.libraryId, sectionUid: a.sectionUid, workTitle: n.title?.split(", ").slice(1).join(", ") || n.libraryId };
    refKey = `b:${n.libraryId}/${a.sectionUid}`;
  } else if (kind === "quote" && n.libraryId) {
    const lines = head
      .split("\n")
      .filter((l) => l.startsWith(">"))
      .map((l) => l.replace(/^>\s?/, ""));
    const by = lines.at(-1) ?? "";
    const text = lines.slice(0, -1).join("\n").trim();
    const m = /^(.*), \*(.*)\*$/.exec(by);
    source = { kind: "quote", text, author: m?.[1] ?? "", work: m?.[2] ?? "", libraryId: n.libraryId, sectionUid: a?.sectionUid ?? null };
    refKey = `q:${n.libraryId}`;
  }
  return { id: n.id, date: isoDay(n.createdAt), refKey, refLabel: n.title ?? refKey, source, answers };
}

const LOCAL_KEY = "bs.reflections";

/** Reflections kept in this browser before sync, read once to move them into notes. */
export function localReflectionsToMigrate(): Reflection[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const items = raw ? (JSON.parse(raw) as Reflection[]) : [];
    return Array.isArray(items) ? items.filter((r) => r && Array.isArray(r.answers) && typeof r.refKey === "string") : [];
  } catch {
    return [];
  }
}

export function forgetLocalReflections() {
  try {
    localStorage.removeItem(LOCAL_KEY);
  } catch {
    // Private windows: nothing was kept.
  }
}
